import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Zap, Sunrise, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOR = {
  EXTREME: "text-red-400 border-red-500/40 bg-red-500/10",
  HIGH: "text-orange-400 border-orange-500/40 bg-orange-500/10",
  MEDIUM: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  LOW: "text-slate-400 border-slate-500/40 bg-slate-500/10",
  MINIMAL: "text-slate-500 border-slate-600/40 bg-slate-600/10",
};

// Trade of the Day: the AuraTrade intraday engine's highest-conviction
// same-day setup for this instrument (independent of the weekly thesis above).
export default function DailyTradeCard({ symbol, decimals = 2 }) {
  const [zones, setZones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fmt = (n) => (n == null ? "\u2014" : n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }));

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    axios.get(`${API}/aura/intraday/zones/${encodeURIComponent(symbol)}`)
      .then((res) => { if (mounted) setZones(res.data); })
      .catch((err) => { if (mounted) { console.error("DailyTradeCard fetch failed:", err); setError(err); } })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [symbol]);

  if (loading) {
    return (
      <Card data-testid="daily-trade-card-loading">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500/30" data-testid="daily-trade-card-error">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-400" /> Trade of the Day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Couldn't load today's setup \u2014 try refreshing the page.
          </p>
        </CardContent>
      </Card>
    );
  }

  const trade = zones?.trade_of_the_day;
  if (!trade) {
    return (
      <Card className="border-slate-500/30" data-testid="daily-trade-card-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-400" /> Trade of the Day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No high-conviction intraday setup right now \u2014 check back after the Asian session closes.
          </p>
        </CardContent>
      </Card>
    );
  }

  const tier = TIER_COLOR[trade.conviction?.tier] || TIER_COLOR.LOW;
  const tags = trade.structure_tags || [];
  const score = trade.conviction?.cot_adjusted_pct ?? trade.conviction?.score_pct;

  return (
    <Card className={`border-2 ${trade.entry_armed ? "border-emerald-400/60" : "border-amber-500/30"}`} data-testid="daily-trade-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-400" /> Trade of the Day
          </CardTitle>
          <div className="flex items-center gap-2">
            {trade.entry_armed && (
              <Badge className="bg-emerald-500/30 text-emerald-200 border-emerald-400/60 animate-pulse" data-testid="daily-trade-entry-armed">
                <Zap className="w-3 h-3 mr-1" />ENTRY ARMED
              </Badge>
            )}
            <span className={`px-2 py-0.5 rounded border text-xs font-medium ${tier}`}>
              {trade.conviction?.emoji} {score}%
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Direction</div>
            <div className={`text-base font-semibold ${trade.direction === "LONG" ? "text-green-400" : "text-red-400"}`}>{trade.direction}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Entry</div>
            <div className="text-base font-mono font-semibold" data-testid="daily-trade-entry">{fmt(trade.entry)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stop</div>
            <div className="text-base font-mono font-semibold text-red-400">{fmt(trade.stop)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target</div>
            <div className="text-base font-mono font-semibold text-green-400" data-testid="daily-trade-target">{fmt(trade.target)}</div>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground" data-testid="daily-trade-rr">
          Risk:Reward {trade.rr_ratio}:1 (minimum 1:3 required to be shown here)
        </div>
        <Progress value={score} className="h-1.5" />
        <div className="flex flex-wrap gap-1.5">
          {tags.includes("bos_confirmed") && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">BOS confirmed</Badge>}
          {tags.includes("choch_warning") && <Badge className="bg-red-500/20 text-red-300 border-red-500/40">\u26a0 CHoCH warning</Badge>}
          {tags.includes("counter_trend") && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">Counter-trend</Badge>}
          {trade.cot_alignment === "confirmed" && <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">COT confirmed</Badge>}
        </div>
        <p className="text-xs text-muted-foreground pt-2 border-t border-border/50">{trade.rationale}</p>
      </CardContent>
    </Card>
  );
}
